import React, { forwardRef, useMemo, useState } from "react";
import styles from "./StockSection.module.css";
import TextInput from "@/components/forms/TextInput/TextInput";
import TypeaheadDropdown from "@/components/forms/TypeaheadDropdown/TypeaheadDropdown";
import IconClose from "@/components/icons/IconClose";
import Button from "@/components/forms/Button/Button";
import { PurchaseSectionProps } from "@/components/VariableBuyCost/constants/types";
import {useBuyCostContext} from "@/contexts/BuyCostContext";
import Divider from "../Divider/Divider";
import DatePicker from "../forms/DatePicker";
import { dayjsInUTC } from "@/utils/dateUtils";

const StockSection = forwardRef<any, PurchaseSectionProps>(({
  title,
  date,
  costs,
  onAddCost,
  onDeleteCost,
  onChangeCost,
  onChangeDate,
  deletable = true,
  onDeleteSection,
  usedDates=[],
}, ref) => {
  const { userCategories } = useBuyCostContext();
  const [datePickerDisabled, setDatePickerDisabled] = useState(false);
  const categoriesOptions = useMemo(() => {
    const defaultOptions = ["Buy Cost", "Prep Cost", "Manufacturing", "Shipping"];
    return Array.from(new Set([...defaultOptions, ...userCategories]));
  }, [userCategories]);

  const totalCost = costs.reduce((sum, cost) => sum + cost.cost, 0);

  const handleDateChange = (value: Date | null) => {
    if (value) {
      onChangeDate(value.toISOString());

      const currentDateString = value.toISOString();
      const otherUsedDates = usedDates.filter(d => d !== date); // Exclude current purchase's date
      setDatePickerDisabled(otherUsedDates.includes(currentDateString));
    } else {
      setDatePickerDisabled(true);
    }
  };

  return (
    <div className={styles.stockSectionContainer}>
      <table className={styles.table}>
        <tbody>
        <tr>
          <td className={styles.tableCell}>
            <span>
              {title}
            </span>
          </td>
          <td className={styles.tableCell}>
            <div className={`${styles.verticalContainer}`}>
              <span className={styles.smallTitleText}>
                Purchase Date
              </span>
              <span>
                <DatePicker
                  ref={ref}
                  className={styles.datePicker}
                  value={date ? dayjsInUTC(date).toDate() : null}
                  onChange={handleDateChange}
                  disabled={datePickerDisabled}
                />
              </span>
            </div>
          </td>
          <td className={styles.tableCell}>
            <div className={`${styles.verticalContainer}`}>
              <span className={styles.smallTitleText}>
                Total cost per unit
              </span>
              <TextInput
                type="text"
                step="0.01"
                className={styles.totalInput}
                disabled
                value={"$" + totalCost.toFixed(2)}
              />
            </div>
          </td>
          <td className={styles.lastTableCell}>

          </td>
        </tr>
        </tbody>
      </table>

      <Divider margin="0"/>

      <table className={styles.table}>
        <thead>
          <tr className={styles.rowDivider}>
            <th className={styles.tableCell}>
              Cost Category
            </th>
            <th className={styles.tableCell}>
              Description (optional)
            </th>
            <th className={styles.tableCell}>
              Cost per unit, $
            </th>
            <th className={styles.lastTableCell}>
            </th>
          </tr>
        </thead>
        <tbody>
          {costs.map((cost, index) => (
            <tr key={index}>
              <td className={styles.costCategoriesTableCell}>
                <TypeaheadDropdown
                  key={index}
                  options={categoriesOptions}
                  placeholder="Type or select cost"
                  onChange={(value) => onChangeCost(index, "category", value)}
                  value={cost.category}
                />
              </td>
              <td className={styles.costCategoriesTableCell}>
                <TextInput
                  type="text"
                  placeholder="Describe the cost"
                  className={styles.smallText}
                  value={cost.description}
                  onChange={(value) => onChangeCost(index,'description', value)}
                />
              </td>
              <td className={styles.costCategoriesTableCell}>
                <TextInput
                  type="number"
                  step="0.01"
                  placeholder="Enter the cost"
                  value={cost.costString}
                  className={styles.smallText}
                  onChange={(value) => {
                      const newCost = !value ? 0 : parseFloat(value);
                      onChangeCost(index, 'cost', newCost);
                      onChangeCost(index, 'costString', value);
                  }}
                />
              </td>
              <td>
                <div className={styles.closeButtonContainer}>
                  <button className={styles.closeButton} onClick={() => onDeleteCost(index)}>
                    <IconClose/>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Divider margin={"0"}/>

      <div className={styles.costCategoriesButtons}>
        <Button type={"secondary"} onClick={onAddCost}>
          Add cost category
        </Button>

        {(deletable && onDeleteSection) && (
          <Button type={"secondary"} onClick={onDeleteSection}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                  d="M12.6666 2.66667H10.3333L9.66658 2H6.33325L5.66659 2.66667H3.33325V4H12.6666M3.99992 12.6667C3.99992 13.0203 4.14039 13.3594 4.39044 13.6095C4.64049 13.8595 4.97963 14 5.33325 14H10.6666C11.0202 14 11.3593 13.8595 11.6094 13.6095C11.8594 13.3594 11.9999 13.0203 11.9999 12.6667V4.66667H3.99992V12.6667Z"
                  fill="#1A1A1A"/>
            </svg>
            Delete
          </Button>
        )}
      </div>
    </div>
  )
});

export default StockSection;